import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import { Search, FileDownload, Print, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { getApiBaseUrl, getLogoUrl } from '../../../config/api';
import { notifyError } from '../../../utils/notify';
import { createAxiosConfig, getInstitutionId, exportToExcelWithBoldHeaders } from '../../../utils/feeUtils';

const API_URL = getApiBaseUrl();

const BankReconciliationReport = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [institution, setInstitution] = useState(null);
  const [user, setUser] = useState(null);
  const [filters, setFilters] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    bankAccount: ''
  });

  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const isSuperAdmin = savedUser?.role === 'super_admin';
        const institutionId = getInstitutionId(savedUser, isSuperAdmin);
        
        const params = {};
        if (institutionId) {
          params.institution = institutionId;
        }

        const response = await axios.get(`${API_URL}/bank-accounts`, createAxiosConfig({ params }));
        setBankAccounts(response.data.data || []);
      } catch (err) {
        console.error('Error fetching bank accounts:', err);
      }
    };
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    // Load existing data from localStorage
    const savedInstitution = localStorage.getItem('selectedInstitution');
    if (savedInstitution) {
      try {
        setInstitution(JSON.parse(savedInstitution));
      } catch (e) {
        console.error('Error parsing institution data');
      }
    }
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Error parsing user data');
      }
    }
  }, []);

  const resolveBankAccount = (item) => {
    if (item.bankAccount) {
      const bId = item.bankAccount._id || item.bankAccount;
      const b = bankAccounts.find(x => x._id === bId);
      if (b) return b;
    }
    
    if (item.bankName) {
      const pName = item.bankName.toLowerCase().trim();
      let exact = bankAccounts.find(b => b.bankName.toLowerCase().trim() === pName);
      if (exact) return exact;
      
      let bestMatch = null, maxLen = 0;
      for (const b of bankAccounts) {
        const bName = b.bankName.toLowerCase().trim();
        if (bName.includes(pName) || pName.includes(bName)) {
          const matchLen = Math.min(bName.length, pName.length);
          if (matchLen > maxLen) {
            maxLen = matchLen;
            bestMatch = b;
          }
        }
      }
      if (bestMatch) return bestMatch;
    }
    
    if (item.remarks) {
      const rName = item.remarks.toLowerCase().trim();
      let bestMatch = null, maxLen = 0;
      for (const b of bankAccounts) {
        const bName = b.bankName.toLowerCase().trim();
        if (rName.includes(bName) && bName.length > maxLen) {
          maxLen = bName.length;
          bestMatch = b;
        }
      }
      if (bestMatch) return bestMatch;
    }
    return null;
  };

  const handleFetchReport = async () => {
    try {
      setLoading(true);
      const config = createAxiosConfig();
      
      const isSuperAdmin = user?.role === 'super_admin';
      const institutionId = getInstitutionId(user, isSuperAdmin);

      const params = {
        startDate: filters.dateFrom,
        endDate: filters.dateTo,
        institution: institutionId
      };

      const response = await axios.get(`${API_URL}/fees/payments`, { ...config, params });
      
      let filteredData = response.data.data || [];
      
      // Filter by bank if selected
      if (filters.bankAccount) {
        filteredData = filteredData.filter(p => {
          if (p.status !== 'completed') return false;
          const resolved = resolveBankAccount(p);
          return resolved && resolved._id === filters.bankAccount;
        });
      } else {
        // Show only bank-related payments for this report if no bank selected
        filteredData = filteredData.filter(p => 
          (['bank_transfer', 'online', 'cheque'].includes(p.paymentMethod) || p.bankName) &&
          p.status === 'completed'
        );
      }
      
      // Aggregate by Voucher Number
      const voucherGroups = filteredData.reduce((acc, current) => {
        const vNum = current.voucherNumber || '0';
        if (!acc[vNum]) {
          acc[vNum] = { ...current };
        } else {
          acc[vNum].amount += current.amount;
        }
        return acc;
      }, {});

      setData(Object.values(voucherGroups));
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getBankShortName = (name) => {
    if (!name) return '';
    const clean = name.trim().toUpperCase();
    if (clean.includes('MEEZAN')) return 'MBL';
    if (clean.includes('HABIB') || clean.includes('HBL')) return 'HBL';
    if (clean.includes('UNITED') || clean.includes('UBL')) return 'UBL';
    if (clean.includes('NATIONAL') || clean.includes('NBP')) return 'NBP';
    if (clean.includes('ALLIED') || clean.includes('ABL')) return 'ABL';
    if (clean.includes('ASKARI') || clean.includes('AKBL')) return 'AKBL';
    if (clean.includes('ALFALAH') || clean.includes('BAFL')) return 'BAFL';
    if (clean.includes('FAYSAL') || clean.includes('FBL')) return 'FBL';
    if (clean.includes('PUNJAB') || clean.includes('BOP')) return 'BOP';
    if (clean.includes('MCB')) return 'MCB';
    if (clean.includes('JS BANK') || clean.includes('JSB')) return 'JSB';
    if (clean.includes('DUBAI') || clean.includes('DIB')) return 'DIB';
    if (clean.includes('STANDARD') || clean.includes('SCB')) return 'SCB';
    if (clean.includes('SONERI')) return 'SBL';
    if (clean.includes('ISLAMI')) return 'BIPL';

    const words = clean.split(/\s+/);
    if (words.length > 1) {
      return words.map(w => w[0]).join('');
    }
    return clean.substring(0, 4);
  };

  const getBankDisplay = (item) => {
    let matchedBank = resolveBankAccount(item);
    
    if (!matchedBank && filters.bankAccount) {
      matchedBank = bankAccounts.find(b => b._id === filters.bankAccount);
    }
    if (!matchedBank && bankAccounts.length === 1) {
      matchedBank = bankAccounts[0];
    }

    const rawBankName = matchedBank ? matchedBank.bankName : (item.bankName || '');
    const shortName = getBankShortName(rawBankName);
    
    const accNum = matchedBank ? matchedBank.accountNumber : '';
    const last3 = accNum && accNum.length >= 3 ? accNum.slice(-3) : accNum;

    if (shortName && last3) {
      return `${shortName}-${last3}`;
    }
    return shortName || last3 || '-';
  };

  const handleExportExcel = () => {
    if (data.length === 0) return;

    const exportData = data.map((item, index) => ({
      'Voucher #': item.voucherNumber || '0',
      'Sr #': index + 1,
      'Std.ID': item.student?.enrollmentNumber || 'N/A',
      'Roll NO': item.student?.rollNumber || 'N/A',
      'Name': item.studentName || item.student?.personalDetails?.name || 'N/A',
      'Father Name': item.fatherName || item.student?.guardianInfo?.fatherName || 'N/A',
      'Class': item.student?.admission?.class?.name || item.student?.class?.name || 'N/S',
      'Bank': getBankDisplay(item),
      'Transaction ID': item.transactionId || '-',
      'Bank.Dep.Date': new Date(item.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
      'Fine': 0,
      'Amount': item.amount
    }));

    const selectedBankObj = bankAccounts.find(b => b._id === filters.bankAccount);
    const bankTitle = selectedBankObj 
      ? `${selectedBankObj.bankName} (${selectedBankObj.accountNumber})` 
      : 'All Accounts';

    exportToExcelWithBoldHeaders(
      XLSX,
      exportData,
      'Fee Collected in Bank',
      `Bank_Reconciliation_${filters.dateFrom}_to_${filters.dateTo}.xlsx`,
      {
        'From': formatDate(filters.dateFrom),
        'To': formatDate(filters.dateTo),
        'Bank Title': bankTitle
      }
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Box>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #report-print-area, #report-print-area * {
              visibility: visible;
            }
            #report-print-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 0;
              margin: 0;
              color: black !important;
            }
            .no-print {
              display: none !important;
            }
            @page {
              size: landscape;
              margin: 10mm;
            }
            table {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            th, td {
              border-bottom: 1px solid #ddd !important;
              padding: 4px !important;
              font-size: 8pt !important;
            }
          }
        `}
      </style>

      {/* Filter Section */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Bank Deposit Date From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Bank Deposit Date To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Bank Account</InputLabel>
              <Select
                value={filters.bankAccount}
                label="Bank Account"
                onChange={(e) => setFilters({ ...filters, bankAccount: e.target.value })}
              >
                <MenuItem value="">All Bank Accounts</MenuItem>
                {bankAccounts.map((acc) => (
                  <MenuItem key={acc._id} value={acc._id}>{acc.bankName} - {acc.accountNumber}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Search />}
              onClick={handleFetchReport}
              sx={{ bgcolor: '#667eea', height: '40px' }}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : data.length > 0 ? (
        <Box id="report-print-area">
          <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            >
              PRINT
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownload />}
              onClick={handleExportExcel}
              sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            >
              EXPORT
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 1 }}>
            {institution?.logo && (
              <Box
                component="img"
                src={getLogoUrl(institution.logo, institution._id)}
                sx={{ height: 60, width: 60, borderRadius: '50%', mb: 1, objectFit: 'cover' }}
              />
            )}
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontFamily: 'serif' }}>
              {institution?.name || 'TIGES - TAJ CAMPUS'}
            </Typography>
            <Typography variant="h6" sx={{ fontFamily: 'serif', mt: -1 }}>
              {institution?.address?.city || 'Islamabad'}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', textDecoration: 'underline', mt: 0.5 }}>
              Fee Collected in Bank
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 1, fontSize: '0.85rem' }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant="body2"><b>From:</b> {formatDate(filters.dateFrom)}</Typography>
              <Typography variant="body2"><b>To:</b> {formatDate(filters.dateTo)}</Typography>
              <Typography variant="body2">
                <b>Account.#</b> {filters.bankAccount ? bankAccounts.find(b => b._id === filters.bankAccount)?.accountNumber : 'All Accounts'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2">
                <b>Print Date:</b> {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} &nbsp; {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderBottomWidth: 2, borderColor: 'black', mb: 0.5 }} />
          
          {Object.entries(data.reduce((acc, current) => {
            const cashier = current.cashierName || current.collectedBy?.name || 'Unknown';
            if (!acc[cashier]) acc[cashier] = [];
            acc[cashier].push(current);
            return acc;
          }, {})).map(([cashier, payments], groupIndex) => (
            <Box key={cashier} sx={{ mb: 3 }}>
              <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid black', mb: 0.5, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Cashier: {cashier}
                </Typography>
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ border: 'none' }}>
                <Table size="small" sx={{ 
                  '& th': { fontWeight: 'bold', borderBottom: '1px solid black', px: 0.5, py: 0.5, fontSize: '0.75rem' },
                  '& td': { borderBottom: 'none', px: 0.5, py: 0.3, fontSize: '0.75rem' }
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Voucher #</TableCell>
                      <TableCell>Sr #</TableCell>
                      <TableCell>Std.ID</TableCell>
                      <TableCell>Roll NO</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Father Name</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Bank.Dep.Date</TableCell>
                      <TableCell align="right">Fine</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((row, index) => (
                      <TableRow key={row._id}>
                        <TableCell>{row.voucherNumber || '0'}</TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.student?.enrollmentNumber || '-'}</TableCell>
                        <TableCell>{row.student?.rollNumber || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.studentName || row.student?.personalDetails?.name || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fatherName || row.student?.guardianInfo?.fatherName || '-'}</TableCell>
                        <TableCell>{row.student?.class?.name || row.student?.admission?.class?.name || '-'}</TableCell>
                        <TableCell>{getBankDisplay(row)}</TableCell>
                        <TableCell>{row.transactionId || '-'}</TableCell>
                        <TableCell>{formatDate(row.paymentDate)}</TableCell>
                        <TableCell align="right">0</TableCell>
                        <TableCell align="right">{row.amount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 1, py: 0.5, display: 'flex', justifyContent: 'flex-end', borderTop: '0.5px dashed gray' }}>
                 <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                   Subtotal for {cashier}: {payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                 </Typography>
              </Box>
            </Box>
          ))}

          <Box sx={{ borderTop: '1px solid black', mt: 1, pt: 1, px: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Total Students: {data.length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Total Amount: {data.reduce((sum, item) => sum + item.amount, 0).toLocaleString()}
            </Typography>
            <Typography variant="body2">
              Page 1 of 1
            </Typography>
          </Box>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }} className="no-print">
          No collection data found for the selected criteria.
        </Alert>
      )}
    </Box>
  );
};

export default BankReconciliationReport;
